<script lang="ts" setup>
import type { PropType } from 'vue'
import HomeFloatingBoxCustomSlider from '~/layers/homepage/components/box/HomeFloatingBoxCustomSlider.vue'
import UVideo from '~/layers/ui/components/UVideo.vue'
import type { THomeSectionCustomSlider } from '~/layers/homepage/data/types'
import type { AutoplayOptions, NavigationOptions, PaginationOptions } from 'swiper/types'
import 'swiper/css/effect-fade'
import UVideoAnimationText from '~/layers/ui/components/UVideoAnimationText.vue'

const props = defineProps({
  hideLink: {
    type: Boolean,
    default: false
  },
  section: {
    type: Object as PropType<THomeSectionCustomSlider>,
    required: true,
  },
})
const { section } = toRefs(props)

const id = useId()

const pagination: PaginationOptions = {
  enabled: true,
  el: `#${id} .swiper-pagination`,
  clickable: true,
}
const navigation: NavigationOptions = {
  enabled: true,
  nextEl: `#${id} .slider__navigation-next`,
  prevEl: `#${id} .slider__navigation-prev`,
}
const autoplay = computed<AutoplayOptions>(() => ({
  delay: section.value.autoplay,
  pauseOnMouseEnter: true,
}))

const currentTime = ref<Record<number, number>>({})
const containerRef = ref(null)
const activeIndex = ref<number>(0)
const swiper = useSwiper(containerRef)
const changeSlideHandler = () => {
  activeIndex.value = swiper.instance.value?.realIndex || 0
}
const activeSlide = computed(() => section.value.slides[activeIndex.value])
const activeBox = computed(() => section.value.boxes[activeIndex.value])
</script>

<template>
  <div :id>
    <p class="fixed-panel__text-center_top fixed-panel__text-center">{{ section.title }}</p>
    <div :class="{ 'slider--dark': activeSlide.isDark }" class="slider slider-catalog slider--home-custom">
      <client-only>
        <swiper-container
          ref="containerRef"
          :autoplay="autoplay"
          :fade-effect="{
            crossFade: true,
          }"
          :pagination="pagination"
          :navigation="navigation"
          :slides-per-view="1"
          :speed="800"
          class="swiper-container"
          effect="fade"
          grab-cursor
          watch-overflow
          @swiperslidechange="changeSlideHandler"
          :loop="true"
        >
          <swiper-slide v-for="(slide, index) in section.slides" :key="slide.name" class="swiper-slide">
            <div :class="{ main__picture_square: slide.isSquad }" class="main__picture">
              <UVideo
                v-if="slide.video"
                :autoplay="activeIndex === index"
                :isPlay="activeIndex === index"
                :poster="slide.poster"
                class="custom-slider-video"
                loop
                muted
                playsinline
                @update:currentTime="(time) => (currentTime[index] = time)"
              >
                <source :src="slide.video!.desktop" media="(min-width:1024px)" type="video/mp4" />
                <source :src="slide.video!.mobile" media="(max-width:1023px)" type="video/mp4" />
                <template #animation>
                  <UVideoAnimationText
                    :currentTime="currentTime[index]"
                    :runTime="slide.video?.runTime"
                    :textBottom="slide.video?.textBottom"
                    :hasAnotherBottomBlock="!!(section.buttonText && section.link)"
                  />
                </template>
                <template v-if="!hideLink && section.buttonText && section.link" #link>
                  <nuxt-link class="custom-slider-video__link" :to="section.link">
                    {{ section.buttonText }}
                  </nuxt-link>
                </template>
              </UVideo>
              <URemotePicture v-else :isLazy="false" :picture="slide.picture" class="u-remote-picture--filter" />
            </div>
          </swiper-slide>
        </swiper-container>
      </client-only>
    </div>

    <HomeFloatingBoxCustomSlider v-if="activeBox" :section="activeBox">
      <template #navigation>
        <span class="slider__navigation-prev" @click.stop>
          <svg-icon name="icon-left" width="20" height="20" />
        </span>
        <span class="slider__navigation-next" @click.stop>
          <svg-icon name="icon-right" width="20" height="20" />
        </span>
      </template>
      <template #scrollbar>
        <div class="slider fixed-panel__slider">
          <div class="swiper-pagination" @click.stop></div>
        </div>
      </template>
    </HomeFloatingBoxCustomSlider>
  </div>
</template>

<style lang="scss">
@use '/assets/scss/vars' as *;
@use '/assets/scss/components/slider' as *;

.slider__navigation-prev,
.slider__navigation-next {
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
}

.slider__navigation-prev {
  left: 0;
}

.slider__navigation-next {
  right: 0;
}

.fixed-panel__actions {
  .slider {
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 2px;

    .swiper-pagination {
      position: relative;
      width: 100%;
      text-align: center;
      height: 2px;

      &-bullet {
        height: 2px;
        display: inline-block;
        height: 100%;

        &::after {
          top: 0;
        }
      }
    }
  }

  .fixed-panel__slider {
    bottom: 6px;

    @media (min-width: 1024px) {
      bottom: 18px;
    }
  }
}

.slider {
  &.slider--home-custom {
    @media (max-width: calc(1024px - 0.001px)) {
      swiper-container.swiper-container {
        min-height: auto;
      }

      swiper-slide.swiper-slide {
        min-height: auto;
      }
    }
  }
}

.fixed-panel__menu {
  z-index: 1;
}

.video-container.custom-slider-video {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.custom-slider-video__link {
  color: white;
  display: block;
  white-space: nowrap;
  font-size: 15px;
  left: 50%;
  line-height: 20px;
  position: absolute;
  text-align: center;
  text-transform: uppercase;
  border-bottom: 1px solid white;
  padding-bottom: 3px;
  transform: translate(-50%, 0);
  z-index: 7;
  bottom: 40px;

  @media ($sm) {
    bottom: 62px;
  }
}
</style>
